import {
  BreakpointConditions,
  Breakpoints,
  BreakpointsType,
  CoreColor,
  DensityType,
  DynamicContrastColor,
  FontWeightKey,
  HEX,
  MediaQuery,
  THSLA,
  ThemeColor,
  ThemeColorKey,
  TypographyKey,
  Unit,
} from "./theme";
import { Theme } from "./theme.css";

/**
 * Calculates hover and active colors for a hex value
 */
export const generateHexInteractionColors = (
  hex: `#${string}`
): {
  active: `#${string}`;
  hover: `#${string}`;
} => {
  return {
    active: `${hex}F2`, // 95%
    hover: `${hex}E6`, // 90%
  };
};

/**
 * Get lightness from the color
 */
const getColorLightness = (hsla: THSLA) => {
  return parseFloat(hsla.split(", ")[2]);
};

/**
 * Get hsla values from the color
 */
export const getHSLAValues = (hsla: THSLA) => {
  const digits = hsla.match(/\(([^)]+)\)/)?.pop() || "";
  const parsed = digits.split(",").map((n) => parseFloat(n) || 0);
  // provide a default 0 structure if something goes really wrong
  return [parsed[0] ?? 0, parsed[1] ?? 0, parsed[2] ?? 0, parsed[3] ?? 0];
};

const updateColorLightness = (color: THSLA, addon: number): THSLA => {
  const [h, s, l, a] = getHSLAValues(color);
  return `hsla(${h}, ${s}%, ${l + addon}%, ${a})`;
};

const hslToRgb = (color: THSLA) => {
  const [h, _s, _l] = getHSLAValues(color);

  const s = _s / 100;
  const l = _l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) =>
    l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));

  return [255 * f(0), 255 * f(8), 255 * f(4)];
};

const relativeLuminance = (color: THSLA) => {
  const [R, G, B] = hslToRgb(color).map((n) => {
    n = n / 255;
    return n <= 0.03928 ? n / 12.92 : Math.pow((n + 0.055) / 1.055, 2.4);
  });
  return R * 0.2126 + G * 0.7152 + B * 0.0722;
};

/** Get contrast color for a given color - white or black */
export const onContrast = (color: THSLA, white: THSLA, black: THSLA) => {
  const colorLuminance = relativeLuminance(color);
  const whiteLuminance = relativeLuminance(white);
  const blackLuminance = relativeLuminance(black);
  const contrastWithWhite = (whiteLuminance + 0.05) / (colorLuminance + 0.05);
  const contrastWithBlack = (colorLuminance + 0.05) / (blackLuminance + 0.05);

  return contrastWithBlack > contrastWithWhite ? black : white;
};

export const onHover = (color: THSLA): THSLA => {
  const lightness = getColorLightness(color);
  if (lightness - 10 > 0) {
    // Decrease lightness by 10 for hover effect
    return updateColorLightness(color, -10);
  } else {
    // If decreasing lightness goes beyond 0, increase lightness by 10
    return updateColorLightness(color, 10);
  }
};

export const onPress = (color: THSLA): THSLA => {
  const lightness = getColorLightness(color);
  if (lightness - 20 > 0) {
    // Decrease lightness by 20 for hover effect
    return updateColorLightness(color, -20);
  } else {
    // If decreasing lightness goes beyond 0, increase lightness by 20
    return updateColorLightness(color, 20);
  }
};

export const onFade = (color: THSLA, alpha = 0.02): THSLA => {
  // To get disabled effect set opacity to 2%
  const [h, s, l] = getHSLAValues(color);
  return `hsla(${h}, ${s}%, ${l}%, ${alpha})`;
};

export const generateContrastColors = (
  coreColors: CoreColor
): DynamicContrastColor => ({
  onBrand: onContrast(
    coreColors.brand as THSLA,
    coreColors.white as THSLA,
    coreColors.black as THSLA
  ),
  onPrimaryText: onContrast(
    coreColors.primaryText as THSLA,
    coreColors.white as THSLA,
    coreColors.black as THSLA
  ),
  outline: onContrast(
    coreColors.containerBackground as THSLA,
    coreColors.white as THSLA,
    coreColors.black as THSLA
  ),
});

export const generateBrandColors = (coreColors: CoreColor) => ({
  brandHover: onHover(coreColors.brand as THSLA),
  brandPressed: onPress(coreColors.brand as THSLA),
});

export const generateInteractiveColors = (coreColors: CoreColor) => ({
  interactiveHover: onHover(coreColors.interactive as THSLA),
  interactivePressed: onPress(coreColors.interactive as THSLA),
});

export const generateCriticalColors = (coreColors: CoreColor) => ({
  criticalHover: onHover(coreColors.critical as THSLA),
  criticalPressed: onPress(coreColors.critical as THSLA),
});

export const generatePrimaryTextColors = (coreColors: CoreColor) => ({
  primaryTextHover: onHover(coreColors.primaryText as THSLA),
  primaryTextPressed: onPress(coreColors.primaryText as THSLA),
});

export const generateBorderColors = (coreColors: CoreColor) => ({
  borderHover: onHover(coreColors.border as THSLA),
  borderPressed: onPress(coreColors.border as THSLA),
});

export const generateContainerBackgroundColors = (coreColors: CoreColor) => ({
  containerBackgroundHover: onHover(coreColors.containerBackground as THSLA),
  containerBackgroundPressed: onPress(coreColors.containerBackground as THSLA),
});

export const generateFormBackgroundColors = (coreColors: CoreColor) => ({
  formBackgroundHover: onHover(coreColors.formBackground as THSLA),
  formBackgroundPressed: onPress(coreColors.formBackground as THSLA),
});

export const generateTanslucentColors = (coreColors: CoreColor) => ({
  interactiveFade: onFade(coreColors.interactive as THSLA, 0.4),
  borderFade: onFade(coreColors.border as THSLA, 0.4),
  dropdownBoxShadow: onFade(coreColors.containerBackground as THSLA, 0.12),
  listItemHighlight: onFade(coreColors.black as THSLA, 0.02),
});

export const generateDynamicColors = (coreColors: CoreColor) => ({
  ...generateContrastColors(coreColors),
  ...generateBrandColors(coreColors),
  ...generateInteractiveColors(coreColors),
  ...generateCriticalColors(coreColors),
  ...generatePrimaryTextColors(coreColors),
  ...generateBorderColors(coreColors),
  ...generateContainerBackgroundColors(coreColors),
  ...generateFormBackgroundColors(coreColors),
  ...generateTanslucentColors(coreColors),
});

export const toHSLA = (color: HEX): THSLA => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color) || [];
  let r = parseInt(result[1], 16);
  let g = parseInt(result[2], 16);
  let b = parseInt(result[3], 16);

  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h = 0,
    s = 0,
    l = (max + min) / 2;
  if (max !== min) {
    const difference = max - min;
    s = l > 0.5 ? difference / (2 - max - min) : difference / (max + min);
    switch (max) {
      case r:
        h = (g - b) / difference + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / difference + 2;
        break;
      case b:
        h = (r - g) / difference + 4;
        break;
    }
    h /= 6;
  }

  h = Number((h * 360).toFixed(2));
  s = Number((s * 100).toFixed(2));
  l = Number((l * 100).toFixed(2));

  return `hsla(${h}, ${s}%, ${l}%, 1)`;
};

export function hexAToHSLA(H: string): THSLA {
  let r = 0,
    g = 0,
    b = 0,
    a = 0;

  if (H.length === 5) {
    r = Number("0x" + H[1] + H[1]);
    g = Number("0x" + H[2] + H[2]);
    b = Number("0x" + H[3] + H[3]);
    a = Number("0x" + H[4] + H[4]);
  } else if (H.length === 9) {
    r = Number("0x" + H[1] + H[2]);
    g = Number("0x" + H[3] + H[4]);
    b = Number("0x" + H[5] + H[6]);
    a = Number("0x" + H[7] + H[8]);
  }

  // Then to HSL
  r /= 255;
  g /= 255;
  b /= 255;
  const cmin = Math.min(r, g, b);
  const cmax = Math.max(r, g, b);
  const delta = cmax - cmin;
  let h = 0;
  let s: number;
  let l: number;

  if (delta == 0) {
    // Do nothing
    // h = 0
  } else if (cmax === r) h = ((g - b) / delta) % 6;
  else if (cmax === g) h = (b - r) / delta + 2;
  else h = (r - g) / delta + 4;

  h = Math.round(h * 60);

  if (h < 0) h += 360;

  l = (cmax + cmin) / 2;
  s = delta == 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
  s = +(s * 100).toFixed(1);
  l = +(l * 100).toFixed(1);
  a = +(a / 255).toFixed(3);

  return `hsla(${h}, ${s}%, ${l}%, ${a})`;
}

export const convertColorsToHSLA = (colors: ThemeColor) => {
  return Object.keys(colors).reduce(
    (newColors, key) => {
      const isHex = colors[key as ThemeColorKey].includes("#");

      let color = colors[key as ThemeColorKey] as THSLA;
      if (isHex) {
        if (color.length === 4 || color.length === 7) {
          color = toHSLA(colors[key as ThemeColorKey] as HEX);
        } else {
          color = hexAToHSLA(colors[key as ThemeColorKey]);
        }
      }

      newColors[key as ThemeColorKey] = color;

      return newColors;
    },
    {} as Record<string, THSLA>
  );
};

export const generateSpacingUnit = (densityValue: DensityType): Unit => {
  const density = {
    compact: 0.75,
    default: 1,
    spacious: 1.25,
  };
  const spacingBaseUnit = 4;
  return `${density[densityValue] * spacingBaseUnit}px`;
};

export const generateConditions = (
  breakpointConditions: BreakpointConditions
) => {
  return Object.keys(breakpointConditions).reduce(
    (newBreakpoints, key) => {
      const value = breakpointConditions[key as BreakpointsType];
      const breakpointObj = value === "" ? {} : { "@media": value };

      newBreakpoints = {
        ...newBreakpoints,
        [key]: breakpointObj,
      };
      return newBreakpoints;
    },
    {} as Record<BreakpointsType, { "@media": MediaQuery }>
  );
};

export const mapBreakpointsToString = (breakpoints: Breakpoints) => {
  return Object.keys(breakpoints).reduce((newBreakpoints, key) => {
    const value = breakpoints[key as BreakpointsType];
    newBreakpoints = {
      ...newBreakpoints,
      [key]: value === 0 ? "" : `(min-width: ${value}px)`,
    };
    return newBreakpoints;
  }, {} as BreakpointConditions);
};

export const generateLetterSpacings = (
  fontSizes: Record<TypographyKey, string>
) => {
  // Values for letter spacing and line height are tried and tested by Design team and are based on the explanation here
  // https://www.figma.com/file/wEMFG5Q26IuCnqwMrti1qr/Typography-Tokens-%5BMSCHECKOUT-2560%5D?type=design&node-id=27%3A6716&t=u5n7VqPlFc6zK4fa-1
  // There is no formula for computing the values for letterSpacing and lineHeight
  const letterSpacingUnits = {
    h1: -0.015,
    h2: -0.01,
    h3: -0.005,
    h4: 0,
    body: 0,
    caption: 0.014,
    button: 0,
  };

  return Object.keys(fontSizes).reduce(
    (res, key) => {
      const value = Number(fontSizes[key as TypographyKey].split("r")[0]);
      res[key as TypographyKey] = `${
        value * letterSpacingUnits[key as TypographyKey]
      }rem`;
      return res;
    },
    {} as Record<TypographyKey, string>
  );
};

export const generateLineHeights = (
  fontSizes: Record<TypographyKey, string>
) => {
  const lineHeightUnits = {
    h1: 1.25,
    h2: 1.33,
    h3: 1.4,
    h4: 1.5,
    body: 1.5,
    caption: 1.57,
    button: 1.5,
  };

  return Object.keys(fontSizes).reduce(
    (res, key) => {
      const value = Number(fontSizes[key as TypographyKey].split("r")[0]);
      res[key as TypographyKey] = `${
        value * lineHeightUnits[key as TypographyKey]
      }rem`;
      return res;
    },
    {} as Record<TypographyKey, string>
  );
};

export const pxFontSizesToRem = (fontSizes: Record<TypographyKey, string>) => {
  return Object.keys(fontSizes).reduce(
    (res, key) => {
      // Pixels aren't always passed so this returns rem if used
      if (fontSizes[key as TypographyKey]?.includes("rem")) {
        res[key as TypographyKey] = fontSizes[key as TypographyKey];
        return res;
      }

      let value = Number(fontSizes[key as TypographyKey]);
      // compute based on pixels
      if (fontSizes[key as TypographyKey]?.includes("px")) {
        value = Number(fontSizes[key as TypographyKey].slice(0, -2));
      }
      //numerical pass through
      res[key as TypographyKey] = `${value / 16}rem`;
      return res;
    },
    {} as Record<TypographyKey, string>
  );
};

export const remFontSizeToPx = (fontSize: string): number => {
  if (isNaN(Number(fontSize))) {
    return Number(fontSize.replace("rem", "")) * 16;
  }
  return Number(fontSize) * 16;
};

export function useButtonDimensions(data: Theme): {
  height: number;
  padding: number;
} {
  const height = remFontSizeToPx(data.lineHeights.button);
  const spacingBaseUnit = 4;
  const padding = Number(data.unit.replace("px", "")) * spacingBaseUnit;

  return {
    height,
    padding,
  };
}

export const generateFontWeights = (
  fontWeights: Record<TypographyKey, FontWeightKey>
) => {
  const fontWeightUnits: Record<FontWeightKey, string> = {
    semibold: "600",
    medium: "500",
    regular: "400",
  };
  return Object.keys(fontWeights).reduce(
    (res, key) => {
      res[key as TypographyKey] =
        fontWeightUnits[fontWeights[key as TypographyKey]];
      return res;
    },
    {} as Record<TypographyKey, string>
  );
};

export const generateFontFamilies = (
  baseFont: string,
  fontFamilies?: Partial<Record<TypographyKey, string>>
) => {
  return {
    h1: fontFamilies?.h1 ?? baseFont,
    h2: fontFamilies?.h2 ?? baseFont,
    h3: fontFamilies?.h3 ?? baseFont,
    h4: fontFamilies?.h4 ?? baseFont,
    body: fontFamilies?.body ?? baseFont,
    caption: fontFamilies?.caption ?? baseFont,
    button: fontFamilies?.button ?? baseFont,
  };
};
